import { ADMIN_ROLE_NAMES } from '@/features/servicePointSelection/utils/servicePointUtils';

/** localStorage key under which the Redux state snapshot envelope is stored. Not a credential. */
const STATE_SNAPSHOT_STORAGE_KEY = 'rwf-reloaded';
let snapshotWriteGeneration = 0;
let pendingSnapshotWrite: Promise<void> = Promise.resolve();

/**
 * Stable identity string used as PBKDF2 password input.
 * Binds the derived key to this application's origin so a snapshot written
 * on one origin cannot be decrypted on another. Not a secret — security comes
 * from AES-GCM's authenticated encryption, not from key secrecy.
 */
const getKeyMaterial = (): string =>
    typeof window !== 'undefined'
        ? `rwf-snapshot-v2|${window.location.origin}`
        : 'rwf-snapshot-v2|ssr';

/**
 * Returns localStorage when running in a browser context, otherwise null.
 * localStorage is shared across tabs for the same origin, which allows a
 * separately opened tab to restore the authenticated shell snapshot.
 */
const getSnapshotStorage = (): Storage | null => {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
};

// ---------------------------------------------------------------------------
// Web Crypto AES-GCM helpers
// ---------------------------------------------------------------------------

/**
 * Derives a non-extractable AES-GCM-256 CryptoKey from the app identity string.
 * Called once at module load; the resulting promise is shared across all usages.
 */
const deriveAesKey = (): Promise<CryptoKey> => {
    if (typeof window === 'undefined' || !window.crypto?.subtle) {
        return Promise.reject(new Error('SubtleCrypto not available'));
    }

    const enc = new TextEncoder();

    return window.crypto.subtle
        .importKey('raw', enc.encode(getKeyMaterial()), 'PBKDF2', false, ['deriveKey'])
        .then((baseKey) =>
            window.crypto.subtle.deriveKey(
                {
                    name: 'PBKDF2',
                    // Fixed salt — binds the key to this storage version.
                    // Not a secret; the PBKDF2 iteration cost is the security property.
                    salt: enc.encode('rwf-snapshot-salt-v2'),
                    iterations: 100_000,
                    hash: 'SHA-256',
                },
                baseKey,
                { name: 'AES-GCM', length: 256 },
                false,
                ['encrypt', 'decrypt'],
            ),
        );
};

/** Module-level key promise — derived once, reused for every read/write. */
const aesKeyPromise: Promise<CryptoKey> = deriveAesKey();

/**
 * Encrypts a UTF-8 string with AES-GCM-256.
 * Returns a base64-encoded JSON envelope containing the random IV and ciphertext.
 */
const encryptText = async (plainText: string): Promise<string> => {
    const key = await aesKeyPromise;
    const iv = window.crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for AES-GCM
    const enc = new TextEncoder();

    const encryptedBuffer = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        enc.encode(plainText),
    );

    const envelope = {
        iv: Array.from(iv),
        ct: Array.from(new Uint8Array(encryptedBuffer)),
    };

    return btoa(JSON.stringify(envelope));
};

/**
 * Decrypts an AES-GCM-256 base64-encoded envelope produced by encryptText.
 */
const decryptText = async (encoded: string): Promise<string> => {
    const key = await aesKeyPromise;
    const envelope = JSON.parse(atob(encoded)) as { iv: number[]; ct: number[] };

    const iv = new Uint8Array(envelope.iv);
    const ct = new Uint8Array(envelope.ct);

    const plainBuffer = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);

    return new TextDecoder().decode(plainBuffer);
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Removes the Redux state snapshot from localStorage immediately.
 * Call this on logout so that a freshly opened tab cannot hydrate a
 * stale authenticated state after the user has signed out.
 */
export const clearStateSnapshot = (): void => {
    // Invalidate writes that are still encrypting so a pending write cannot
    // recreate an authenticated snapshot after logout clears it.
    snapshotWriteGeneration += 1;
    const storage = getSnapshotStorage();
    if (!storage) return;
    storage.removeItem(STATE_SNAPSHOT_STORAGE_KEY);
};

/**
 * Reads and decrypts the Redux state snapshot from localStorage.
 * The snapshot is intentionally kept in localStorage so that duplicated tabs,
 * freshly opened tabs, and reloads can all restore the authenticated shell.
 */
export const readStateSnapshot = async <T>(): Promise<T | undefined> => {
    try {
        const storage = getSnapshotStorage();
        if (!storage) return undefined;

        const raw = storage.getItem(STATE_SNAPSHOT_STORAGE_KEY);
        if (!raw) return undefined;

        const plainText = await decryptText(raw);
        return JSON.parse(plainText) as T;
    } catch {
        // Corrupt, tampered, or legacy-format entry — treat as absent.
        clearStateSnapshot();
        return undefined;
    }
};

/**
 * Encrypts and writes the Redux state snapshot into localStorage using AES-GCM-256.
 */
export const writeStateSnapshot = <T>(value: T): Promise<void> => {
    const storage = getSnapshotStorage();
    if (!storage) return Promise.resolve();

    const plainText = JSON.stringify(value);
    const generation = snapshotWriteGeneration;
    const write = pendingSnapshotWrite
        .catch(() => undefined)
        .then(async () => {
            if (generation !== snapshotWriteGeneration) return;

            const encrypted = await encryptText(plainText);
            if (generation !== snapshotWriteGeneration) return;

            storage.setItem(STATE_SNAPSHOT_STORAGE_KEY, encrypted);
        });
    pendingSnapshotWrite = write;
    return write;
};

interface SnapshotStoreLike<TState> {
    getState: () => TState;
    subscribe: (listener: () => void) => () => void;
}

interface StateSnapshotPersistenceOptions<TState, TSnapshot extends Record<string, unknown>> {
    store: SnapshotStoreLike<TState>;
    selectPersistableState: (state: TState) => TSnapshot;
}

let isStateSnapshotPersistenceRegistered = false;
let persistCurrentSnapshot: (() => void) | null = null;

/**
 * Writes the current shell snapshot immediately.
 *
 * Call this when a persisted slice changes in a way that a duplicated or newly
 * opened tab must see right away (e.g. service-point switch). The snapshot is
 * otherwise only written on login and `beforeunload`, so a long-lived tab that
 * changed its service point would leave a stale profile for duplicated tabs.
 * No-op until `initializeStateSnapshotPersistence` has registered the store.
 */
export const persistStateSnapshotNow = (): void => {
    persistCurrentSnapshot?.();
};

/**
 * Persists a selected snapshot of the Redux state to localStorage.
 *
 * Persistence strategy:
 * - Written on `beforeunload`
 * - Written immediately through `persistStateSnapshotNow()` for context changes
 *   that other tabs must restore (service point)
 * - No continuous subscription-based writes to minimize I/O
 * - New/duplicate tabs restore the last stable state from the previous session
 * - Open tabs stay in sync via Redux broadcast middleware
 */
export const initializeStateSnapshotPersistence = <
    TState,
    TSnapshot extends Record<string, unknown>,
>({
    store,
    selectPersistableState,
}: StateSnapshotPersistenceOptions<TState, TSnapshot>): void => {
    if (typeof window === 'undefined' || isStateSnapshotPersistenceRegistered) return;

    const persistSnapshot = () => {
        // Only write when authenticated — an unauthenticated tab must not
        // overwrite the valid snapshot written by an authenticated tab.
        const finalState = selectPersistableState(store.getState());

        type SnapShape = Record<string, unknown> & {
            authStore?: {
                isAuthenticated?: boolean;
                user?: { roles?: { roles?: Array<{ name?: string }> } } | null;
            };
            servicePointProfile?: { profile?: { srvPntFcId?: string } | null };
        };
        const snap = finalState as SnapShape;
        const isAuth = snap?.authStore?.isAuthenticated;
        if (!isAuth) return;

        // Non-admin users (agent roles) who have not yet selected a service point
        // are in the "SP selection" intermediate state — still authenticated in
        // memory so the selection screen renders, but the snapshot must NOT carry
        // isAuthenticated:true or a reload would skip the login form and they
        // could reach /main without a real session.
        const primaryRoleName = snap?.authStore?.user?.roles?.roles?.[0]?.name ?? '';
        const isAgentUser = primaryRoleName !== '' && !ADMIN_ROLE_NAMES.includes(primaryRoleName);
        const hasSp = Boolean(snap?.servicePointProfile?.profile?.srvPntFcId);

        const snapshotToWrite =
            isAgentUser && !hasSp
                ? { ...finalState, authStore: { ...snap.authStore, isAuthenticated: false } }
                : finalState;

        // writeStateSnapshot is async; fire-and-forget is acceptable here because
        // the browser grants a short grace period for beforeunload async work and
        // a missed persist is non-critical (the user will re-authenticate).
        writeStateSnapshot(snapshotToWrite).catch(() => {
            // Ignore persistence failures to avoid blocking page unload.
        });
    };

    persistCurrentSnapshot = persistSnapshot;
    window.addEventListener('beforeunload', persistSnapshot);

    isStateSnapshotPersistenceRegistered = true;
};
